<!-- =========================================================================================
    File Name: ScreenAnchor3dScene.vue
    Description: Unified editor for all screen anchors - handles multiple screen configurations
    (landscape/portrait, phone/tablet) in a single view with anchor selector
========================================================================================== -->

<template>
  <div>
    <vx-card class="card-no-padding">
      <!-- Header -->
      <div class="screen-anchor-header">
        <div class="vx-row align-center">
          <div class="vx-col w-full">
            <h5 class="mb-0">
              <feather-icon icon="MonitorIcon" svgClasses="w-5 h-5 mr-2" />
              Screen Anchors
              <vs-chip v-if="selectedAnchor" color="primary" class="ml-2">
                {{ getAnchorLabel(selectedAnchor) }}
              </vs-chip>
            </h5>
          </div>
        </div>
      </div>

      <!-- Loading indicator -->
      <div v-if="isLoading" class="center-top-right-loading">
        <div class="contained-example-container">
          <div id="div-with-loading" class="vs-con-loading__container"></div>
        </div>
      </div>

      <!-- Main canvas area -->
      <div class="screen-anchor-canvas-wrapper">
        <screen-anchor-canvas
          v-if="selectedAnchor"
          :anchor="selectedAnchor"
          :nodes="nodesForSelectedAnchor"
          :hobjects="hobjects"
          :selectedNodePid.sync="selectedNodePid"
          @node-drag-start="onNodeDragStart"
          @node-resize-start="onNodeResizeStart"
          @node-moved="onNodeMoved"
          @node-resized="onNodeResized"
          @hobject-dropped="onHobjectDropped"
        />
        <div v-else class="no-anchor-selected">
          <feather-icon icon="MonitorIcon" svgClasses="w-16 h-16 mb-4 opacity-50" />
          <p>Select or create a screen anchor configuration</p>
        </div>
      </div>

      <!-- Screen Anchor Selector/Manager (left sidebar) -->
      <div class="anchor-selector-sidebar" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
        <div class="sidebar-toggle" @click="sidebarCollapsed = !sidebarCollapsed">
          <feather-icon :icon="sidebarCollapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'" svgClasses="w-4 h-4" />
        </div>

        <div v-if="!sidebarCollapsed" class="sidebar-content">
          <p class="config-section-title">Screen Configurations</p>

        <div class="anchor-list">
          <div
            v-for="anchor in screenAnchors"
            :key="anchor.pid"
            class="anchor-item"
            :class="{ 'anchor-item--selected': selectedAnchor && selectedAnchor.pid === anchor.pid }"
            @click="selectAnchor(anchor)"
          >
            <div class="anchor-item-icon">
              <feather-icon :icon="getAnchorIcon(anchor)" svgClasses="w-4 h-4" />
            </div>
            <div class="anchor-item-info">
              <div class="anchor-item-label">{{ getAnchorLabel(anchor) }}</div>
              <div class="anchor-item-meta">{{ getAnchorConfig(anchor).referenceWidth }}×{{ getAnchorConfig(anchor).referenceHeight }}</div>
            </div>
            <div class="anchor-item-actions">
              <feather-icon
                icon="TrashIcon"
                svgClasses="w-3 h-3 text-danger cursor-pointer"
                @click.stop="confirmRemoveAnchor(anchor)"
              />
            </div>
          </div>
        </div>

        <vs-button
          size="small"
          type="border"
          color="primary"
          class="w-full mt-2"
          @click="showAddAnchorDialog = true"
        >
          <feather-icon icon="PlusIcon" svgClasses="w-4 h-4 mr-1" />
          Add Configuration
        </vs-button>

        <!-- Current anchor settings -->
        <div v-if="selectedAnchor" class="mt-4">
          <p class="config-section-title">Aspect Ratio</p>
          <div class="vx-row radio-group-horizontal">
            <div class="vx-col w-full">
              <vs-radio
                v-model="currentConfig.aspectRatioClass"
                vs-value="landscape"
                @change="updateAspectRatio('landscape')"
              >Landscape</vs-radio>
              <vs-radio
                v-model="currentConfig.aspectRatioClass"
                vs-value="portrait"
                @change="updateAspectRatio('portrait')"
              >Portrait</vs-radio>
            </div>
          </div>

          <p class="config-section-title mt-3">Device Class</p>
          <div class="vx-row radio-group-horizontal">
            <div class="vx-col w-full">
              <vs-radio
                v-model="currentConfig.deviceClass"
                vs-value="phone"
                @change="updateDeviceClass('phone')"
              >Phone</vs-radio>
              <vs-radio
                v-model="currentConfig.deviceClass"
                vs-value="tablet"
                @change="updateDeviceClass('tablet')"
              >Tablet</vs-radio>
            </div>
          </div>

          <p class="config-section-title mt-3">Scale Mode</p>
          <div class="vx-row radio-group-horizontal">
            <div class="vx-col w-full">
              <vs-radio
                v-model="currentConfig.scaleMode"
                vs-value="letterbox"
                @change="updateAnchorConfig('scaleMode', 'letterbox')"
              >Letterbox</vs-radio>
              <vs-radio
                v-model="currentConfig.scaleMode"
                vs-value="fill"
                @change="updateAnchorConfig('scaleMode', 'fill')"
              >Fill</vs-radio>
            </div>
          </div>
        </div>
        </div>
      </div>

      <!-- Object properties panel (right sidebar when node is selected) -->
      <div v-if="selectedNode" class="properties-sidebar">
        <screen-anchor-object-properties
          :node="selectedNode"
          :hobjects="hobjects"
          @update-node="onNodeUpdate"
        />
      </div>
    </vx-card>

    <!-- Add Anchor Dialog -->
    <vs-popup title="Add Screen Anchor Configuration" :active.sync="showAddAnchorDialog">
      <div class="p-4">
        <div class="vx-row mb-3">
          <div class="vx-col w-full">
            <label class="input-label">Aspect Ratio</label>
            <vs-select v-model="newAnchorConfig.aspectRatioClass" class="w-full mt-1">
              <vs-select-item value="landscape" text="Landscape" />
              <vs-select-item value="portrait" text="Portrait" />
            </vs-select>
          </div>
        </div>

        <div class="vx-row mb-3">
          <div class="vx-col w-full">
            <label class="input-label">Device Class</label>
            <vs-select v-model="newAnchorConfig.deviceClass" class="w-full mt-1">
              <vs-select-item value="phone" text="Phone" />
              <vs-select-item value="tablet" text="Tablet" />
            </vs-select>
          </div>
        </div>

        <div class="vx-row">
          <div class="vx-col w-full text-right">
            <vs-button color="danger" type="flat" @click="showAddAnchorDialog = false">Cancel</vs-button>
            <vs-button color="primary" @click="addNewAnchor">Create</vs-button>
          </div>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import ScreenAnchorCanvas from './screen-anchor/ScreenAnchorCanvas.vue'
import ScreenAnchorObjectProperties from './screen-anchor/ScreenAnchorObjectProperties.vue'
import * as Command from '@/assets/js/utils/commands.js'

const DEFAULT_CONFIG = {
  referenceWidth: 1920,
  referenceHeight: 1080,
  anchorPosition: 'bottom-right',
  scaleMode: 'letterbox',
  safeArea: true,
  aspectRatioClass: 'landscape',
  deviceClass: 'phone',
}

export default {
  components: {
    ScreenAnchorCanvas,
    ScreenAnchorObjectProperties,
  },

  props: {
    screenAnchors: { type: Array, required: true }, // All screen anchors for this space
    nodes: { type: Array, default: () => [] },      // All nodes for this space
    hobjects: { type: Array, default: () => [] },
    space: { type: Object, required: true },
    selectedNodes: { type: Array, default: () => [] },
  },

  data() {
    return {
      isLoading: true,
      selectedAnchor: null,
      selectedNodePid: null,
      showAddAnchorDialog: false,
      sidebarCollapsed: false,
      newAnchorConfig: {
        aspectRatioClass: 'landscape',
        deviceClass: 'phone',
      },
    }
  },

  computed: {
    currentConfig() {
      if (!this.selectedAnchor) return { ...DEFAULT_CONFIG }
      // Always get the latest anchor from the store by PID to ensure reactivity
      const latestAnchor = this.screenAnchors.find(a => a.pid === this.selectedAnchor.pid)
      return this.getAnchorConfig(latestAnchor || this.selectedAnchor)
    },

    nodesForSelectedAnchor() {
      if (!this.selectedAnchor) return []
      return this.nodes.filter(n => n.anchor_pid === this.selectedAnchor.pid)
    },

    selectedNode() {
      if (!this.selectedNodePid) return null
      return this.nodesForSelectedAnchor.find(n => n.pid === this.selectedNodePid) || null
    },
  },

  watch: {
    selectedNodePid(newPid, oldPid) {
      if (newPid !== oldPid) {
        if (newPid) {
          const node = this.nodes.find(n => n.pid === newPid)
          if (node) this.$emit('addNodeToSelection', node)
        } else if (oldPid) {
          const node = this.nodes.find(n => n.pid === oldPid)
          if (node) this.$emit('removeNodeFromSelection', node)
        }
      }
    },

    selectedNodes: {
      handler(newNodes) {
        if (newNodes.length === 1) {
          // Find which anchor this node belongs to
          const nodeAnchor = this.screenAnchors.find(a => a.pid === newNodes[0].anchor_pid)
          if (nodeAnchor) {
            this.selectedAnchor = nodeAnchor
            this.selectedNodePid = newNodes[0].pid
          }
        } else if (newNodes.length === 0) {
          this.selectedNodePid = null
        }
      },
      immediate: true,
    },

    screenAnchors: {
      immediate: true,
      deep: true,
      handler(anchors) {
        // Auto-select first anchor if none selected
        if (anchors.length > 0 && !this.selectedAnchor) {
          this.selectedAnchor = anchors[0]
          this.$emit('anchor-selected', anchors[0])
        }
        // Keep selectedAnchor in sync with the store when anchor is updated
        if (this.selectedAnchor) {
          const updatedAnchor = anchors.find(a => a.pid === this.selectedAnchor.pid)
          if (updatedAnchor) {
            this.selectedAnchor = updatedAnchor
          }
        }
      },
    },
  },

  mounted() {
    this.$vs.loading({
      container: '#div-with-loading',
      scale: 0.6,
    })

    this.$nextTick(() => {
      this.$vs.loading.close('#div-with-loading > .con-vs-loading')
      this.isLoading = false
      // Emit the selected anchor to parent after mounting
      if (this.selectedAnchor) {
        console.log('[ScreenAnchor3dScene] Emitting anchor-selected on mount:', this.selectedAnchor)
        this.$emit('anchor-selected', this.selectedAnchor)
      }
    })
  },

  methods: {
    // Compute dimensions based on aspect ratio and device class
    getDimensionsForConfig(aspectRatioClass, deviceClass) {
      // Phone landscape: 1920x1080, portrait: 1080x1920
      // Tablet landscape: 2732x2048, portrait: 2048x2732
      const dimensions = {
        phone: {
          landscape: { width: 1920, height: 1080 },
          portrait: { width: 1080, height: 1920 },
        },
        tablet: {
          landscape: { width: 2732, height: 2048 },
          portrait: { width: 2048, height: 2732 },
        },
      }

      if (dimensions[deviceClass] && dimensions[deviceClass][aspectRatioClass]) {
        return dimensions[deviceClass][aspectRatioClass]
      }
      return { width: 1920, height: 1080 }
    },

    getAnchorConfig(anchor) {
      try {
        return { ...DEFAULT_CONFIG, ...JSON.parse(anchor.mark) }
      } catch {
        return { ...DEFAULT_CONFIG }
      }
    },

    getAnchorLabel(anchor) {
      const config = this.getAnchorConfig(anchor)
      const aspect = config.aspectRatioClass ? config.aspectRatioClass.charAt(0).toUpperCase() + config.aspectRatioClass.slice(1) : 'Unknown'
      const device = config.deviceClass ? config.deviceClass.charAt(0).toUpperCase() + config.deviceClass.slice(1) : 'Unknown'
      return `${aspect} ${device}`
    },

    getAnchorIcon(anchor) {
      const config = this.getAnchorConfig(anchor)
      if (config.deviceClass === 'phone') return 'SmartphoneIcon'
      if (config.deviceClass === 'tablet') return 'TabletIcon'
      return 'SmartphoneIcon'
    },

    selectAnchor(anchor) {
      // Force reactivity by creating a new reference
      this.selectedAnchor = null
      this.$nextTick(() => {
        this.selectedAnchor = anchor
        this.selectedNodePid = null
        console.log('[ScreenAnchor3dScene] Anchor manually selected, emitting:', anchor)
        this.$emit('anchor-selected', anchor)
      })
    },

    updateAspectRatio(aspectRatioClass) {
      const dimensions = this.getDimensionsForConfig(aspectRatioClass, this.currentConfig.deviceClass)
      const updated = {
        ...this.currentConfig,
        aspectRatioClass,
        referenceWidth: dimensions.width,
        referenceHeight: dimensions.height,
        safeArea: true, // Always true by default
      }
      const updatedAnchor = {
        ...this.selectedAnchor,
        mark: JSON.stringify(updated),
        name: this.selectedAnchor.name || 'Screen Anchor',
        anchor_type: this.selectedAnchor.anchor_type || 'screen_anchor',
        is_dirty: true, // Mark anchor as dirty for save detection
      }

      console.log('[ScreenAnchor3dScene] updateAspectRatio - updating store:', updatedAnchor)

      // Update the anchor in the store without saving to server
      this.$store.commit('hoverlay/UPDATE_ANCHOR', updatedAnchor)
    },

    updateDeviceClass(deviceClass) {
      const dimensions = this.getDimensionsForConfig(this.currentConfig.aspectRatioClass, deviceClass)
      const updated = {
        ...this.currentConfig,
        deviceClass,
        referenceWidth: dimensions.width,
        referenceHeight: dimensions.height,
        safeArea: true, // Always true by default
      }
      const updatedAnchor = {
        ...this.selectedAnchor,
        mark: JSON.stringify(updated),
        name: this.selectedAnchor.name || 'Screen Anchor',
        anchor_type: this.selectedAnchor.anchor_type || 'screen_anchor',
        is_dirty: true, // Mark anchor as dirty for save detection
      }

      console.log('[ScreenAnchor3dScene] updateDeviceClass - updating store:', updatedAnchor)

      // Update the anchor in the store without saving to server
      this.$store.commit('hoverlay/UPDATE_ANCHOR', updatedAnchor)
    },

    updateAnchorConfig(key, value) {
      if (!this.selectedAnchor) return

      const updated = { ...this.currentConfig, [key]: value }
      const updatedAnchor = {
        ...this.selectedAnchor,
        mark: JSON.stringify(updated),
        name: this.selectedAnchor.name || 'Screen Anchor',
        anchor_type: this.selectedAnchor.anchor_type || 'screen_anchor',
        is_dirty: true, // Mark anchor as dirty for save detection
      }

      console.log('[ScreenAnchor3dScene] updateAnchorConfig - updating store:', updatedAnchor)

      // Update the anchor in the store without saving to server
      this.$store.commit('hoverlay/UPDATE_ANCHOR', updatedAnchor)
    },

    async addNewAnchor() {
      try {
        // Save nodes from currently selected anchor to duplicate them
        const nodesToDuplicate = this.selectedAnchor ? [...this.nodesForSelectedAnchor] : []

        // Compute dimensions based on selected aspect ratio and device class
        const dimensions = this.getDimensionsForConfig(
          this.newAnchorConfig.aspectRatioClass,
          this.newAnchorConfig.deviceClass
        )

        const mark = JSON.stringify({
          ...DEFAULT_CONFIG,
          aspectRatioClass: this.newAnchorConfig.aspectRatioClass,
          deviceClass: this.newAnchorConfig.deviceClass,
          referenceWidth: dimensions.width,
          referenceHeight: dimensions.height,
          safeArea: true, // Always true by default
        })

        const newAnchor = await this.$store.dispatch('hoverlay/addAnchor', {
          name: `Screen Anchor - ${this.newAnchorConfig.aspectRatioClass} ${this.newAnchorConfig.deviceClass}`,
          anchor_type: 'screen_anchor',
          mark,
          access_level: 'everyone',
        })

        await this.$store.dispatch('hoverlay/addAnchorSpace', {
          anchor_pid: newAnchor.pid,
          space_pid: this.space.pid,
        })

        // Duplicate nodes from the previous anchor to the new anchor
        if (nodesToDuplicate.length > 0) {
          for (const node of nodesToDuplicate) {
            await this.$store.dispatch('hoverlay/createNodeInSpace', {
              pid: this.space.pid,
              anchor_pid: newAnchor.pid,
              hobject_pid: node.hobject_pid,
              x: node.x,
              y: node.y,
              z: node.z || 0,
              angle_x: node.angle_x || 0,
              angle_y: node.angle_y || 0,
              angle_z: node.angle_z || 0,
              scale: node.scale || 1.0,
              width: node.width,
              height: node.height,
              anchor_position: node.anchor_position,
            })
          }
        }

        this.selectedAnchor = newAnchor
        this.showAddAnchorDialog = false

        // Reset form
        this.newAnchorConfig = {
          aspectRatioClass: 'landscape',
          deviceClass: 'phone',
        }

        this.$vs.notify({
          title: 'Screen Anchor added',
          text: 'New screen configuration created',
          color: 'success',
          iconPack: 'feather',
          icon: 'icon-smartphone',
        })
      } catch (err) {
        console.error('[ScreenAnchor3dScene] addNewAnchor failed', err)
        this.$vs.notify({ title: 'Error', text: 'Failed to create screen anchor', color: 'danger' })
      }
    },

    confirmRemoveAnchor(anchor) {
      this.$emit('remove-anchor', anchor)
    },

    onNodeDragStart() {
      // The TransformNodeToCommand will handle storing the before state
    },

    onNodeResizeStart() {
      // The TransformNodeToCommand will handle storing the before state
    },

    onNodeMoved(updatedNode) {
      this.updateNode(updatedNode)
    },

    onNodeResized(updatedNode) {
      this.updateNode(updatedNode)
    },

    onNodeUpdate(updatedNode) {
      this.updateNode(updatedNode)
    },

    updateNode(updatedNode) {
      // Find the original node from the store
      const originalNode = this.nodes.find(n => n.pid === updatedNode.pid)
      if (!originalNode) return

      // Create a command for undo/redo
      const command = new Command.TransformNodeToCommand(originalNode, updatedNode)

      // Emit the command to parent for undo/redo tracking
      this.$emit('updateNodeTransform', command)
    },

    async onHobjectDropped({ hobject_pid, x, y, width, height, anchor_position }) {
      if (!this.selectedAnchor) return

      // Get hobject to determine natural dimensions if not provided
      const hobject = this.hobjects.find(h => h.pid === hobject_pid)
      const defaultWidth = 200
      const defaultHeight = 200

      try {
        await this.$store.dispatch('hoverlay/createNodeInSpace', {
          pid: this.space.pid,
          anchor_pid: this.selectedAnchor.pid,
          hobject_pid,
          x: x || 0,
          y: y || 0,
          z: 0,
          angle_x: 0,
          angle_y: 0,
          angle_z: 0,
          scale: 1.0,
          width: width || (hobject && hobject.width) || defaultWidth,
          height: height || (hobject && hobject.height) || defaultHeight,
          anchor_position: anchor_position || 'bottom-right',
        })
      } catch (err) {
        console.error('[ScreenAnchor3dScene] onHobjectDropped failed', err)
        this.$vs.notify({ title: 'Error', text: 'Failed to add object to canvas', color: 'danger' })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.screen-anchor-header {
  padding: 16px 20px;
  background: #f9f9f9;
  border-bottom: 1px solid #e0e0e0;
}

.screen-anchor-canvas-wrapper {
  position: relative;
  height: 600px;
  background: #1a1a2e;
}

.no-anchor-selected {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
}

.anchor-selector-sidebar {
  position: absolute;
  top: 72px;
  left: 0;
  width: 280px;
  max-height: calc(100% - 72px);
  background: rgba(255, 255, 255, 0.98);
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  transition: all 0.3s ease;

  &.sidebar-collapsed {
    width: 48px;
    padding: 0;
    overflow: visible;

    .sidebar-content {
      display: none;
    }
  }
}

.sidebar-toggle {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #7367f0;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  z-index: 11;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #5e50d8;
    transform: scale(1.05);
  }
}

.sidebar-collapsed .sidebar-toggle {
  right: 8px;
  left: 8px;
  margin: auto;
}

.sidebar-content {
  padding: 16px;
  padding-top: 40px;
}

.anchor-list {
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 8px;
}

.anchor-item {
  display: flex;
  align-items: center;
  padding: 10px;
  margin-bottom: 4px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #7367f0;
  }

  &--selected {
    background: #7367f0;
    border-color: #7367f0;
    color: white;

    .anchor-item-label,
    .anchor-item-meta {
      color: white;
    }
  }
}

.anchor-item-icon {
  margin-right: 10px;
}

.anchor-item-info {
  flex: 1;
}

.anchor-item-label {
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 2px;
}

.anchor-item-meta {
  font-size: 0.7rem;
  color: #888;
}

.anchor-item-actions {
  margin-left: 8px;
}

.properties-sidebar {
  position: absolute;
  top: 72px;
  right: 0;
  width: 280px;
  max-height: calc(100% - 72px);
  background: rgba(255, 255, 255, 0.98);
  border-left: 1px solid #e0e0e0;
  padding: 16px;
  overflow-y: auto;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.config-section-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 8px 0;
}

.input-label {
  font-size: 0.75rem;
  color: #626262;
  font-weight: 500;
  margin-bottom: 4px;
}

.center-top-right-loading {
  position: absolute;
  right: 20px;
  top: 20px;
  z-index: 100;
}

#div-with-loading {
  height: 50px;
  width: 50px;
}

#div-with-loading > .con-vs-loading {
  background-color: rgba(255, 255, 128, 0);
}

.card-no-padding > .vx-card__collapsible-content > .vx-card__body {
  padding: 0rem !important;
}

.align-center {
  align-items: center;
}

.text-right {
  text-align: right;
}

/* Horizontal radio button layout with spacing */
.radio-group-horizontal {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.radio-group-horizontal .vs-radio {
  display: inline-flex;
  margin-top: 0 !important;
}
</style>
